// Lógica pura (sin React/Firebase) para validar que un atleta inscrito
// está en una categoría que corresponde a su edad, y que su dorsal está
// dentro del rango definido para su modalidad. Solo informa (no bloquea) —
// la decisión de corregir algo queda en manos del admin.
//
// La edad en sí se calcula con calcularEdad (services/utils.jsx), ya usado
// en la descarga de Excel y en IncomeRow.jsx — acá no se reinventa esa
// regla, solo se compara el resultado contra el rango de la categoría.

// Mismo criterio de lookup que resolverCategoriaLabel
// (Configuration/BulkEdit/bulkEditLogic.js), pero devuelve el objeto
// completo de la categoría (para leer edadDesde/edadHasta), no solo su
// nombre.
export function buscarCategoria(eventSubEventsRegistry, codMod, rama, codCat) {
  if (!eventSubEventsRegistry || codMod === undefined || codMod === null || codMod === "") {
    return null;
  }
  const modalidad = eventSubEventsRegistry["modalidad_" + codMod];
  const categoria =
    modalidad &&
    modalidad.ramaCat &&
    modalidad.ramaCat["rama_" + rama] &&
    modalidad.ramaCat["rama_" + rama].categorias &&
    modalidad.ramaCat["rama_" + rama].categorias["categoria_" + codCat];
  return categoria || null;
}

// 200 es la convención ya usada en el proyecto para "sin límite superior"
// (ver DashboardModRamCat.jsx, que arma el texto "(edadDesde y mayores)"
// cuando edadHasta === 200).
const SIN_LIMITE_SUPERIOR = 200;

export function validarEdadEnCategoria(edad, categoria) {
  if (!categoria || categoria.edadDesde === undefined || categoria.edadHasta === undefined) {
    // sin rango de edad definido para esta categoría, nada que validar
    return { valido: true, mensaje: "" };
  }

  const dentroDeRango =
    edad >= categoria.edadDesde && (categoria.edadHasta === SIN_LIMITE_SUPERIOR || edad <= categoria.edadHasta);

  if (dentroDeRango) {
    return { valido: true, mensaje: "" };
  }

  const rangoTexto =
    categoria.edadHasta === SIN_LIMITE_SUPERIOR
      ? `${categoria.edadDesde} años o más`
      : `${categoria.edadDesde} a ${categoria.edadHasta} años`;

  return {
    valido: false,
    mensaje: `La edad calculada (${edad}) no corresponde al rango de la categoría "${categoria.nombreCategoria || ""}" (${rangoTexto}).`,
  };
}

export function validarDorsalEnRango(numero, folios) {
  if (!folios || folios.desde === undefined || folios.hasta === undefined) {
    // modalidad sin rango de folios definido, nada que validar
    return { valido: true, mensaje: "" };
  }
  if (numero === undefined || numero === null || numero === "" || Number(numero) <= 0) {
    // todavía no tiene dorsal asignado, nada que validar
    return { valido: true, mensaje: "" };
  }

  const numeroNum = Number(numero);
  const dentroDeRango = numeroNum >= folios.desde && numeroNum <= folios.hasta;

  if (dentroDeRango) {
    return { valido: true, mensaje: "" };
  }

  return {
    valido: false,
    mensaje: `El dorsal ${numeroNum} está fuera del rango definido para esta modalidad (${folios.desde} a ${folios.hasta}). Si el rango de folios de esta distancia se modificó recientemente, puedes hacer caso omiso de esta advertencia.`,
  };
}

// Umbral y muestra mínima para considerar que una modalidad "cambió de
// rango" en vez de tener dorsales mal asignados uno por uno: es común,
// cerca de la fecha del evento, que el cliente ajuste el rango de folios de
// una distancia — eso deja a todos o casi todos los dorsales ya asignados
// "fuera de rango" de golpe, lo cual no es un error real. Con una muestra
// chica (pocos inscritos con dorsal en esa modalidad) no se aplica esta
// excepción, para no ocultar un problema real de asignación.
const UMBRAL_RANGO_CAMBIADO = 0.7; // 70% o más fuera de rango
const MUESTRA_MINIMA_RANGO_CAMBIADO = 3;

// Para cada modalidad, calcula si la proporción de dorsales fuera de rango
// es tan alta que indica un cambio de rango (y no errores individuales) —
// devuelve un set ({ [modalidad]: true }) de las modalidades a exceptuar.
// Se usa solo en el listado de inscritos (no en el detalle del atleta, que
// siempre muestra la alerta real por participante).
export function calcularModalidadesConRangoDorsalCambiado(
  eventParticipants,
  eventSubEventsRegistry
) {
  const conteos = {};

  for (const key in eventParticipants || {}) {
    const p = eventParticipants[key];
    if (!p || p.borrado) continue;
    if (!p.statusPago || p.statusPago.status !== "ok") continue;

    const numero = p.numero;
    if (numero === undefined || numero === null || numero === "" || Number(numero) <= 0) {
      continue;
    }

    const modalidad = p.modalidad;
    const configModalidad =
      eventSubEventsRegistry &&
      eventSubEventsRegistry["modalidad_" + modalidad] &&
      eventSubEventsRegistry["modalidad_" + modalidad].configModalidad;
    const folios = configModalidad && configModalidad.folios;
    if (!folios || folios.desde === undefined || folios.hasta === undefined) continue;

    if (conteos[modalidad] === undefined) {
      conteos[modalidad] = { total: 0, fueraDeRango: 0 };
    }
    conteos[modalidad].total++;
    if (!validarDorsalEnRango(numero, folios).valido) {
      conteos[modalidad].fueraDeRango++;
    }
  }

  const modalidadesExentas = {};
  Object.keys(conteos).forEach((modalidad) => {
    const { total, fueraDeRango } = conteos[modalidad];
    if (
      total >= MUESTRA_MINIMA_RANGO_CAMBIADO &&
      fueraDeRango / total >= UMBRAL_RANGO_CAMBIADO
    ) {
      modalidadesExentas[modalidad] = true;
    }
  });

  return modalidadesExentas;
}
